/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const CoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<any[]>([]);
  const [lessons, setLessons] = useState<any[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  const [totalCourses, setTotalCourses] = useState(0);
  const [totalLessons, setTotalLessons] = useState(0);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  
  // Simple cache to prevent duplicate API calls
  const [coursesCache, setCoursesCache] = useState<{[key: number]: any}>({});
  const [lessonsCache, setLessonsCache] = useState<{[key: string]: any}>({});

  // Pagination states for courses
  const [coursesCurrentPage, setCoursesCurrentPage] = useState(1);
  const [coursesTotalPages, setCoursesTotalPages] = useState(1);
  const [coursesPerPage] = useState(10);

  // Pagination states for lessons
  const [lessonsCurrentPage, setLessonsCurrentPage] = useState(1);
  const [lessonsTotalPages, setLessonsTotalPages] = useState(1);
  const [lessonsPerPage] = useState(10);

  const [showCreateCourseForm, setShowCreateCourseForm] = useState(false);
  const [showCreateLessonForm, setShowCreateLessonForm] = useState(false);
  const [showEditCourseForm, setShowEditCourseForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<any>(null);
  const [showEditLessonForm, setShowEditLessonForm] = useState(false);
  const [editingLesson, setEditingLesson] = useState<any>(null);
  const [showDeleteLessonConfirm, setShowDeleteLessonConfirm] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState<any>(null);
  const [showLessonDetails, setShowLessonDetails] = useState(false);
  const [lessonDetails, setLessonDetails] = useState<any>(null);
  const [lessonDetailsLoading, setLessonDetailsLoading] = useState(false);

  // Course form states
  const [courseTitle, setCourseTitle] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  const [courseCategory, setCourseCategory] = useState('');
  const [courseCustomCategory, setCourseCustomCategory] = useState('');
  const [courseDifficulty, setCourseDifficulty] = useState('Beginner');
  const [courseDuration, setCourseDuration] = useState('');
  const [courseError, setCourseError] = useState('');
  const [courseSuccess, setCourseSuccess] = useState('');

  // Lesson form states
  const [lessonTitle, setLessonTitle] = useState('');
  const [lessonDescription, setLessonDescription] = useState('');
  const [lessonNumber, setLessonNumber] = useState('');
  const [lessonDuration, setLessonDuration] = useState('');
  const [lessonContent, setLessonContent] = useState('');
  const [lessonVideoUrl, setLessonVideoUrl] = useState('');
  const [lessonKeyTakeaways, setLessonKeyTakeaways] = useState('');
  const [lessonError, setLessonError] = useState('');
  const [lessonSuccess, setLessonSuccess] = useState('');

  // Edit course form states
  const [editCourseTitle, setEditCourseTitle] = useState('');
  const [editCourseDescription, setEditCourseDescription] = useState('');
  const [editCourseCategory, setEditCourseCategory] = useState('');
  const [editCourseCustomCategory, setEditCourseCustomCategory] = useState('');
  const [editCourseDifficulty, setEditCourseDifficulty] = useState('Beginner');
  const [editCourseDuration, setEditCourseDuration] = useState('');
  const [editCourseError, setEditCourseError] = useState('');
  const [editCourseSuccess, setEditCourseSuccess] = useState('');

  // Edit lesson form states
  const [editLessonTitle, setEditLessonTitle] = useState('');
  const [editLessonDescription, setEditLessonDescription] = useState('');
  const [editLessonNumber, setEditLessonNumber] = useState('');
  const [editLessonDuration, setEditLessonDuration] = useState('');
  const [editLessonContent, setEditLessonContent] = useState('');
  const [editLessonVideoUrl, setEditLessonVideoUrl] = useState('');
  const [editLessonKeyTakeaways, setEditLessonKeyTakeaways] = useState('');
  const [editLessonError, setEditLessonError] = useState('');
  const [editLessonSuccess, setEditLessonSuccess] = useState('');

  const fetchCourses = async (page: number = 1) => {
    // Check cache first
    if (coursesCache[page]) {
      console.log('📦 Using cached courses for page', page);
      const cachedData = coursesCache[page];
      setCourses(cachedData.courses);
      setTotalCourses(cachedData.total);
      setCoursesTotalPages(cachedData.pages);
      setCoursesCurrentPage(cachedData.page);
      setTotalLessons(cachedData.totalLessons);
      return;
    }

    setCoursesLoading(true);
    try {
      const response = await services.get(
        `${API_ENDPOINTS.COURSES.LIST}?page=${page}&limit=${coursesPerPage}`
      );
      const responseData = response.data;

      // Extract data and pagination info from your API response format
      const coursesData = responseData.data || [];
      const paginationInfo = {
        total: responseData.total,
        pages: responseData.pages,
        page: responseData.page,
        count: responseData.count,
      };

      console.log('📊 Courses loaded:', coursesData.length, 'of', paginationInfo.total);

      setCourses(coursesData);
      setTotalCourses(paginationInfo.total);
      setCoursesTotalPages(paginationInfo.pages);
      setCoursesCurrentPage(paginationInfo.page);

      // OPTIMIZED: Use a single API call to get total lessons count
      // This is much faster than making individual calls for each course
      try {
        const lessonsCountResponse = await services.get(
          `${API_ENDPOINTS.LESSONS.LIST}?page=1&limit=1`
        );
        const totalLessonsCount = lessonsCountResponse.data.total || 0;
        setTotalLessons(totalLessonsCount);
        console.log('📊 Total lessons count:', totalLessonsCount);
        
        // Cache the results
        setCoursesCache(prev => ({
          ...prev,
          [page]: {
            courses: coursesData,
            total: paginationInfo.total,
            pages: paginationInfo.pages,
            page: paginationInfo.page,
            totalLessons: totalLessonsCount
          }
        }));
      } catch (err) {
        console.log('Could not fetch total lessons count:', err);
        setTotalLessons(0);
      }
    } catch (err: any) {
      console.error('Failed to fetch courses:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
    } finally {
      setCoursesLoading(false);
    }
  };

  const fetchLessons = async (courseId: string, page: number = 1) => {
    const cacheKey = `${courseId}-${page}`;
    
    // Check cache first
    if (lessonsCache[cacheKey]) {
      console.log('📦 Using cached lessons for course', courseId, 'page', page);
      const cachedData = lessonsCache[cacheKey];
      setLessons(cachedData.lessons);
      setLessonsTotalPages(cachedData.pages);
      setLessonsCurrentPage(cachedData.page);
      return;
    }

    setLessonsLoading(true);
    try {
      const response = await services.get(
        `${API_ENDPOINTS.LESSONS.GET_BY_COURSE(courseId)}&page=${page}&limit=${lessonsPerPage}`
      );
      const responseData = response.data;

      // Extract data and pagination info from your API response format
      const lessonsData = responseData.data || [];
      const paginationInfo = {
        total: responseData.total,
        pages: responseData.pages,
        page: responseData.page,
        count: responseData.count,
      };

      console.log('📚 Lessons loaded:', lessonsData.length, 'of', paginationInfo.total);

      setLessons(lessonsData);
      setLessonsTotalPages(paginationInfo.pages);
      setLessonsCurrentPage(paginationInfo.page);
      
      // Cache the results
      setLessonsCache(prev => ({
        ...prev,
        [cacheKey]: {
          lessons: lessonsData,
          pages: paginationInfo.pages,
          page: paginationInfo.page
        }
      }));
    } catch (err: any) {
      console.error('Failed to fetch lessons:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
    } finally {
      setLessonsLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses(1);
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      setLessonsCurrentPage(1); // Reset to first page when switching courses
      fetchLessons(selectedCourse._id || selectedCourse.id, 1);
    }
  }, [selectedCourse]);

  const handleCourseClick = (course: any) => {
    setSelectedCourse(course);
  };

  const handleBackToCourses = () => {
    setSelectedCourse(null);
    setLessons([]);
    setLessonsCurrentPage(1);
    setLessonsTotalPages(1);
  };

  // Pagination handlers for courses
  const handleCoursesPageChange = (page: number) => {
    setCoursesCurrentPage(page);
    fetchCourses(page);
  };

  // Pagination handlers for lessons
  const handleLessonsPageChange = (page: number) => {
    setLessonsCurrentPage(page);
    if (selectedCourse) {
      fetchLessons(selectedCourse._id || selectedCourse.id, page);
    }
  };

  // Course management functions
  const openCreateCourseForm = () => {
    setShowCreateCourseForm(true);
    setCourseError('');
    setCourseSuccess('');
  };

  const closeCreateCourseForm = () => {
    setShowCreateCourseForm(false);
    setCourseTitle('');
    setCourseDescription('');
    setCourseCategory('');
    setCourseCustomCategory('');
    setCourseDifficulty('Beginner');
    setCourseDuration('');
    setCourseError('');
    setCourseSuccess('');
  };

  const onSubmitCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    setCourseError('');
    setCourseSuccess('');

    const finalCategory = courseCategory === 'Other' ? courseCustomCategory : courseCategory;

    if (!courseTitle || !courseDescription || !finalCategory || !courseDuration) {
      setCourseError('All fields are required');
      return;
    }

    try {
      await services.post(API_ENDPOINTS.COURSES.CREATE, {
        title: courseTitle,
        description: courseDescription,
        category: finalCategory,
        difficulty: courseDifficulty,
        duration: parseInt(courseDuration),
        isPublished: true,
      });
      setCourseSuccess('Course created successfully!');
      fetchCourses();
      setTimeout(() => {
        closeCreateCourseForm();
      }, 1500);
    } catch (err: any) {
      console.error('Failed to create course:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setCourseError(err.data?.message || 'Failed to create course');
    }
  };

  const handleEditCourse = (course: any) => {
    setEditingCourse(course);
    setEditCourseTitle(course.title || '');
    setEditCourseDescription(course.description || '');

    // Check if the category is one of the predefined options
    const predefinedCategories = [
      'Real Estate',
      'Physical Assets',
      'security',
      'NFT and Digital',
      'Advanced',
    ];
    if (predefinedCategories.includes(course.category)) {
      setEditCourseCategory(course.category || '');
      setEditCourseCustomCategory('');
    } else {
      setEditCourseCategory('Other');
      setEditCourseCustomCategory(course.category || '');
    }

    setEditCourseDifficulty(course.difficulty || 'Beginner');
    setEditCourseDuration(course.duration?.toString() || '');
    setEditCourseError('');
    setEditCourseSuccess('');
    setShowEditCourseForm(true);
  };

  const handleDeleteCourse = (course: any) => {
    setCourseToDelete(course);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCourse = async () => {
    if (!courseToDelete) return;

    try {
      await services.delete(API_ENDPOINTS.COURSES.DELETE(courseToDelete._id || courseToDelete.id));
      fetchCourses();
      setShowDeleteConfirm(false);
      setCourseToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete course:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
    }
  };

  const cancelDeleteCourse = () => {
    setShowDeleteConfirm(false);
    setCourseToDelete(null);
  };

  const closeEditCourseForm = () => {
    setShowEditCourseForm(false);
    setEditingCourse(null);
    setEditCourseTitle('');
    setEditCourseDescription('');
    setEditCourseCategory('');
    setEditCourseCustomCategory('');
    setEditCourseDifficulty('Beginner');
    setEditCourseDuration('');
    setEditCourseError('');
    setEditCourseSuccess('');
  };

  const onSubmitEditCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditCourseError('');
    setEditCourseSuccess('');

    const finalEditCategory =
      editCourseCategory === 'Other' ? editCourseCustomCategory : editCourseCategory;

    if (!editCourseTitle || !editCourseDescription || !finalEditCategory || !editCourseDuration) {
      setEditCourseError('All fields are required');
      return;
    }

    try {
      await services.put(API_ENDPOINTS.COURSES.UPDATE(editingCourse._id || editingCourse.id), {
        title: editCourseTitle,
        description: editCourseDescription,
        category: finalEditCategory,
        difficulty: editCourseDifficulty,
        duration: parseInt(editCourseDuration),
        isPublished: editingCourse.isPublished,
      });
      setEditCourseSuccess('Course updated successfully!');
      fetchCourses();
      setTimeout(() => {
        closeEditCourseForm();
      }, 1500);
    } catch (err: any) {
      console.error('Failed to update course:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setEditCourseError(err.data?.message || 'Failed to update course');
    }
  };

  // Lesson management functions
  const openCreateLessonForm = () => {
    setShowCreateLessonForm(true);
    setLessonError('');
    setLessonSuccess('');
  };

  const closeCreateLessonForm = () => {
    setShowCreateLessonForm(false);
    setLessonTitle('');
    setLessonDescription('');
    setLessonNumber('');
    setLessonDuration('');
    setLessonContent('');
    setLessonVideoUrl('');
    setLessonKeyTakeaways('');
    setLessonError('');
    setLessonSuccess('');
  };

  const onSubmitLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    setLessonError('');
    setLessonSuccess('');

    if (!lessonTitle || !lessonDescription || !lessonNumber || !lessonDuration || !lessonContent) {
      setLessonError('All fields are required');
      return;
    }

    try {
      const keyTakeawaysArray = lessonKeyTakeaways
        .split('\n')
        .map((takeaway) => takeaway.trim())
        .filter((takeaway) => takeaway.length > 0);

      await services.post(API_ENDPOINTS.LESSONS.CREATE, {
        title: lessonTitle,
        description: lessonDescription,
        course: selectedCourse._id || selectedCourse.id,
        lessonNumber: parseInt(lessonNumber),
        duration: parseInt(lessonDuration),
        content: lessonContent,
        videoUrl: lessonVideoUrl,
        keyTakeaways: keyTakeawaysArray,
        isPublished: true,
      });
      setLessonSuccess('Lesson created successfully!');
      if (selectedCourse) {
        fetchLessons(selectedCourse._id || selectedCourse.id);
      }
      setTimeout(() => {
        closeCreateLessonForm();
      }, 1500);
    } catch (err: any) {
      console.error('Failed to create lesson:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setLessonError(err.data?.message || 'Failed to create lesson');
    }
  };

  const handleEditLesson = (lesson: any) => {
    setEditingLesson(lesson);
    setEditLessonTitle(lesson.title || '');
    setEditLessonDescription(lesson.description || '');
    setEditLessonNumber(lesson.lessonNumber?.toString() || '');
    setEditLessonDuration(lesson.duration?.toString() || '');
    setEditLessonContent(lesson.content || '');
    setEditLessonVideoUrl(lesson.videoUrl || '');
    setEditLessonKeyTakeaways(lesson.keyTakeaways?.join('\n') || '');
    setEditLessonError('');
    setEditLessonSuccess('');
    setShowEditLessonForm(true);
  };

  const handleDeleteLesson = (lesson: any) => {
    setLessonToDelete(lesson);
    setShowDeleteLessonConfirm(true);
  };

  const confirmDeleteLesson = async () => {
    if (!lessonToDelete) return;

    try {
      await services.delete(API_ENDPOINTS.LESSONS.DELETE(lessonToDelete._id || lessonToDelete.id));
      if (selectedCourse) {
        fetchLessons(selectedCourse._id || selectedCourse.id);
      }
      setShowDeleteLessonConfirm(false);
      setLessonToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete lesson:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
    }
  };

  const cancelDeleteLesson = () => {
    setShowDeleteLessonConfirm(false);
    setLessonToDelete(null);
  };

  const closeEditLessonForm = () => {
    setShowEditLessonForm(false);
    setEditingLesson(null);
    setEditLessonTitle('');
    setEditLessonDescription('');
    setEditLessonNumber('');
    setEditLessonDuration('');
    setEditLessonContent('');
    setEditLessonVideoUrl('');
    setEditLessonKeyTakeaways('');
    setEditLessonError('');
    setEditLessonSuccess('');
  };

  const onSubmitEditLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditLessonError('');
    setEditLessonSuccess('');

    if (
      !editLessonTitle ||
      !editLessonDescription ||
      !editLessonNumber ||
      !editLessonDuration ||
      !editLessonContent
    ) {
      setEditLessonError('All fields are required');
      return;
    }

    try {
      const keyTakeawaysArray = editLessonKeyTakeaways
        .split('\n')
        .map((takeaway) => takeaway.trim())
        .filter((takeaway) => takeaway.length > 0);

      await services.put(API_ENDPOINTS.LESSONS.UPDATE(editingLesson._id || editingLesson.id), {
        title: editLessonTitle,
        description: editLessonDescription,
        lessonNumber: parseInt(editLessonNumber),
        duration: parseInt(editLessonDuration),
        content: editLessonContent,
        videoUrl: editLessonVideoUrl,
        keyTakeaways: keyTakeawaysArray,
        isPublished: editingLesson.isPublished,
      });
      setEditLessonSuccess('Lesson updated successfully!');
      if (selectedCourse) {
        fetchLessons(selectedCourse._id || selectedCourse.id);
      }
      setTimeout(() => {
        closeEditLessonForm();
      }, 1500);
    } catch (err: any) {
      console.error('Failed to update lesson:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setEditLessonError(err.data?.message || 'Failed to update lesson');
    }
  };

  const handleViewLessonDetails = async (lesson: any) => {
    setLessonDetailsLoading(true);
    setShowLessonDetails(true);
    try {
      const response = await services.get(API_ENDPOINTS.LESSONS.GET_BY_ID(lesson._id || lesson.id));
      setLessonDetails(response.data.data || response.data);
    } catch (err: any) {
      console.error('Failed to fetch lesson details:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setLessonDetails(lesson);
    } finally {
      setLessonDetailsLoading(false);
    }
  };

  const closeLessonDetails = () => {
    setShowLessonDetails(false);
    setLessonDetails(null);
  };

  return (
    <div>
      {!selectedCourse ? (
        <div>
          {/* Header */}
          <div className='mb-8'>
            <h2
              className='text-3xl font-bold mb-2 font-dm-serif'
              style={{
                color: '#ede8c5',
                textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
                letterSpacing: '0.5px',
              }}
            >
              Courses Management
            </h2>
            <p className='font-work-sans' style={{ color: '#ede8c5' }}>
              Create, edit, and manage courses and lessons
            </p>
          </div>

          {/* Statistics Cards */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-8'>
            {/* Total Courses Card */}
            <div
              className='rounded-lg p-6 border shadow-lg transition-all duration-300 hover:scale-105'
              style={{
                backgroundColor: '#0B222C',
                borderColor: '#095D52',
                boxShadow:
                  '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.1) 50%, rgba(11, 34, 44, 0.95) 100%)',
              }}
            >
              <div className='flex items-center'>
                <div className='p-3 rounded-lg mr-4 transition-all duration-300 hover:scale-110'>
                  <svg
                    className='w-6 h-6'
                    style={{ color: '#ede8c5' }}
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM8 15v-4h4v4H8zm6-6H6V8h8v1z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div>
                  <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                    Total Courses
                  </p>
                  <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                    {totalCourses}
                  </p>
                </div>
              </div>
            </div>

            {/* Total Lessons Card */}
            <div
              className='rounded-lg p-6 border shadow-lg transition-all duration-300 hover:scale-105'
              style={{
                backgroundColor: '#0B222C',
                borderColor: '#095D52',
                boxShadow:
                  '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.1) 50%, rgba(11, 34, 44, 0.95) 100%)',
              }}
            >
              <div className='flex items-center'>
                <div
                  className='p-3 rounded-lg mr-4 transition-all duration-300 hover:scale-110'
                  style={{
                    backgroundColor: '#095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                >
                  <svg
                    className='w-6 h-6'
                    style={{ color: '#ede8c5' }}
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                </div>
                <div>
                  <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                    Total Lessons
                  </p>
                  <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                    {totalLessons}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Add Course Button */}
          <div className='flex justify-end mb-8'>
            <Button
              onClick={openCreateCourseForm}
              className='hover:opacity-80 font-work-sans transition-all duration-300 hover:scale-105'
              style={{
                backgroundColor: '#095D52',
                border: '1px solid #095D52',
                color: '#ede8c5',
                boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#bbe425';
                e.currentTarget.style.boxShadow =
                  '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.boxShadow =
                  '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
              }}
            >
              Add Course
            </Button>
          </div>

          {coursesLoading ? (
            <div className='flex items-center justify-center py-12'>
              <div className='text-center'>
                <div
                  className='animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4'
                  style={{ borderColor: '#095D52' }}
                ></div>
                <p className='text-lg font-work-sans' style={{ color: '#ede8c5' }}>
                  Loading courses...
                </p>
              </div>
            </div>
          ) : (
            <div
              className='rounded-lg overflow-hidden shadow-lg transition-all duration-300'
              style={{
                backgroundColor: '#0B222C',
                border: '2px solid #095D52',
                boxShadow:
                  '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
              }}
            >
              <div className='overflow-x-auto'>
                <table className='min-w-full'>
                  <thead
                    style={{
                      backgroundColor: '#095D52',
                      backgroundImage:
                        'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <tr>
                      <th
                        className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Title
                      </th>
                      <th
                        className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Category
                      </th>
                      <th
                        className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Difficulty
                      </th>
                      <th
                        className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Duration
                      </th>
                      <th
                        className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Enrolled
                      </th>
                      <th
                        className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Status
                      </th>
                      <th
                        className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    style={{ backgroundColor: '#0B222C' }}
                    className='divide-y divide-gray-700'
                  >
                    {courses.map((course: any) => (
                      <tr
                        key={course._id || course.id}
                        className='transition-colors cursor-pointer'
                        onClick={() => handleCourseClick(course)}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#095D52')}
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = 'transparent')
                        }
                      >
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='text-sm font-medium' style={{ color: '#ede8c5' }}>
                            {String(course.title || '')}
                          </div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='text-sm' style={{ color: '#ede8c5' }}>
                            {String(course.category || '')}
                          </div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <span
                            className='inline-flex px-2 py-1 text-xs font-semibold rounded-full'
                            style={{
                              backgroundColor: '#095D52',
                              border: '1px solid #095D52',
                              color: '#ede8c5',
                              boxShadow:
                                '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                            }}
                          >
                            {String(course.difficulty || '')}
                          </span>
                        </td>
                        <td
                          className='px-6 py-4 whitespace-nowrap text-sm'
                          style={{ color: '#ede8c5' }}
                        >
                          {course.duration
                            ? course.duration >= 60
                              ? `${Math.floor(course.duration / 60)}h ${course.duration % 60}m`
                              : `${course.duration}m`
                            : '0m'}
                        </td>
                        <td
                          className='px-6 py-4 whitespace-nowrap text-sm'
                          style={{ color: '#ede8c5' }}
                        >
                          {String(
                            course.enrolledCount ||
                              course.enrolled ||
                              course.students ||
                              course.enrollments ||
                              course.studentCount ||
                              course.enrollmentCount ||
                              0
                          )}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <span
                            className='inline-flex px-2 py-1 text-xs font-semibold rounded-full'
                            style={{
                              backgroundColor: '#095D52',
                              border: '1px solid #095D52',
                              color: '#ede8c5',
                              boxShadow:
                                '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                            }}
                          >
                            {course.isPublished ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='flex space-x-3'>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditCourse(course);
                              }}
                              className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none'
                              style={{
                                backgroundColor: '#0B222C',
                                border: '1px solid #095D52',
                                color: '#ede8c5',
                                boxShadow:
                                  '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#095D52';
                                e.currentTarget.style.borderColor = '#bbe425';
                                e.currentTarget.style.boxShadow =
                                  '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#0B222C';
                                e.currentTarget.style.borderColor = '#095D52';
                                e.currentTarget.style.boxShadow =
                                  '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                              }}
                              title='Edit Course'
                            >
                              <svg
                                className='w-4 h-4'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                                />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCourse(course);
                              }}
                              className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none'
                              style={{
                                backgroundColor: '#0B222C',
                                border: '1px solid #095D52',
                                color: '#ede8c5',
                                boxShadow:
                                  '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#EF4444';
                                e.currentTarget.style.borderColor = '#DC2626';
                                e.currentTarget.style.boxShadow =
                                  '0 4px 15px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(220, 38, 38, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#0B222C';
                                e.currentTarget.style.borderColor = '#095D52';
                                e.currentTarget.style.boxShadow =
                                  '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                              }}
                              title='Delete Course'
                            >
                              <svg
                                className='w-4 h-4'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Courses Pagination */}
          {coursesTotalPages > 1 && (
            <div className='mt-6' style={{ borderTop: '1px solid #465705' }}>
              <div className='flex justify-between items-center pt-4'>
                <div className='text-sm' style={{ color: '#ede8c5' }}>
                  Page {coursesCurrentPage} of {coursesTotalPages} • Showing{' '}
                  {(coursesCurrentPage - 1) * coursesPerPage + 1} to{' '}
                  {Math.min(coursesCurrentPage * coursesPerPage, totalCourses)} of {totalCourses}{' '}
                  courses
                </div>

                <div className='flex space-x-2'>
                  <button
                    onClick={() => handleCoursesPageChange(coursesCurrentPage - 1)}
                    disabled={coursesCurrentPage === 1}
                    className='flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    style={{
                      backgroundColor: coursesCurrentPage === 1 ? '#095D52' : '#0B222C',
                      border: '1px solid #bbe425',
                      color: '#ede8c5',
                    }}
                    onMouseEnter={(e) => {
                      if (coursesCurrentPage !== 1) {
                        e.currentTarget.style.backgroundColor = '#bbe425';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (coursesCurrentPage !== 1) {
                        e.currentTarget.style.backgroundColor = '#0c222d';
                      }
                    }}
                  >
                    <svg
                      className='w-4 h-4 mr-2'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 19l-7-7 7-7'
                      />
                    </svg>
                    Previous
                  </button>

                  <button
                    onClick={() => handleCoursesPageChange(coursesCurrentPage + 1)}
                    disabled={coursesCurrentPage === coursesTotalPages}
                    className='flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    style={{
                      backgroundColor:
                        coursesCurrentPage === coursesTotalPages ? '#095D52' : '#bbe425',
                      border: '1px solid #bbe425',
                      color: '#ede8c5',
                    }}
                    onMouseEnter={(e) => {
                      if (coursesCurrentPage !== coursesTotalPages) {
                        e.currentTarget.style.backgroundColor = '#ede8c5';
                        e.currentTarget.style.color = '#0c222d';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (coursesCurrentPage !== coursesTotalPages) {
                        e.currentTarget.style.backgroundColor = '#bbe425';
                        e.currentTarget.style.color = '#ede8c5';
                      }
                    }}
                  >
                    Next
                    <svg
                      className='w-4 h-4 ml-2'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M9 5l7 7-7 7'
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className='flex justify-between items-center mb-6'>
            <div className='flex items-center space-x-4'>
              <button
                onClick={handleBackToCourses}
                className='p-2 rounded-lg transition-colors'
                style={{
                  backgroundColor: '#0c222d',
                  border: '1px solid #bbe425',
                  color: '#ede8c5',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#bbe425')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#095D52')}
              >
                <svg className='w-6 h-6' fill='currentColor' viewBox='0 0 20 20'>
                  <path
                    fillRule='evenodd'
                    d='M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z'
                    clipRule='evenodd'
                  />
                </svg>
              </button>
              <div>
                <h2 className='text-2xl font-bold' style={{ color: '#ede8c5' }}>
                  {String(selectedCourse.title || '')}
                </h2>
                <p style={{ color: '#ede8c5' }}>Lessons Management</p>
              </div>
            </div>
            <Button
              onClick={openCreateLessonForm}
              className='hover:bg-purple-700 font-work-sans transition-all duration-300 hover:scale-105'
              style={{
                backgroundColor: '#095D52',
                border: '1px solid #095D52',
                color: '#ede8c5',
                boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#bbe425';
                e.currentTarget.style.boxShadow =
                  '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.boxShadow =
                  '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
              }}
            >
              Add Lesson
            </Button>
          </div>

          {lessonsLoading ? (
            <div className='flex items-center justify-center py-12'>
              <div className='text-center'>
                <div
                  className='animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4'
                  style={{ borderColor: '#465705' }}
                ></div>
                <p className='#ede8c5 text-lg'>Loading lessons...</p>
              </div>
            </div>
          ) : (
            <div
              className='rounded-lg overflow-hidden shadow-lg transition-all duration-300'
              style={{
                backgroundColor: '#0B222C',
                border: '2px solid #095D52',
                boxShadow:
                  '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
              }}
            >
              <div className='overflow-x-auto'>
                <table className='min-w-full'>
                  <thead
                    style={{
                      backgroundColor: '#095D52',
                      backgroundImage:
                        'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <tr>
                      <th
                        className='px-6 py-3 text-left text-xs font-bold uppercase tracking-wider'
                        style={{ color: '#ede8c5' }}
                      >
                        Lesson #
                      </th>
                      <th
                        className='px-6 py-3 text-left text-xs font-bold uppercase tracking-wider'
                        style={{ color: '#ede8c5' }}
                      >
                        Title
                      </th>
                      <th
                        className='px-6 py-3 text-left text-xs font-bold uppercase tracking-wider'
                        style={{ color: '#ede8c5' }}
                      >
                        Duration
                      </th>
                      <th
                        className='px-6 py-3 text-left text-xs font-bold uppercase tracking-wider'
                        style={{ color: '#ede8c5' }}
                      >
                        Video
                      </th>
                      <th
                        className='px-6 py-3 text-left text-xs font-bold uppercase tracking-wider'
                        style={{ color: '#ede8c5' }}
                      >
                        Completions
                      </th>
                      <th
                        className='px-6 py-3 text-left text-xs font-bold uppercase tracking-wider'
                        style={{ color: '#ede8c5' }}
                      >
                        Status
                      </th>
                      <th
                        className='px-6 py-3 text-left text-xs font-bold uppercase tracking-wider'
                        style={{ color: '#ede8c5' }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y' style={{ backgroundColor: '#0B222C' }}>
                    {lessons.map((lesson: any) => (
                      <tr
                        key={lesson._id || lesson.id}
                        className='transition-colors'
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#095D52')}
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = 'transparent')
                        }
                      >
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='text-sm font-medium' style={{ color: '#ede8c5' }}>
                            {String(lesson.lessonNumber || 0)}
                          </div>
                        </td>
                        <td className='px-6 py-4'>
                          <div className='text-sm font-medium' style={{ color: '#ede8c5' }}>
                            {String(lesson.title || '')}
                          </div>
                          <div
                            className='text-sm max-w-xs truncate'
                            style={{ color: '#ede8c5' }}
                            title={String(lesson.description || '')}
                          >
                            {String(lesson.description || '')}
                          </div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm #ede8c5'>
                          {lesson.duration
                            ? lesson.duration >= 60
                              ? `${Math.floor(lesson.duration / 60)}h ${lesson.duration % 60}m`
                              : `${lesson.duration}m`
                            : '0m'}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          {lesson.videoUrl ? (
                            <a
                              href={lesson.videoUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-blue-400 hover:text-blue-300 text-sm'
                            >
                              Watch Video
                            </a>
                          ) : (
                            <span className='text-sm' style={{ color: '#ede8c5' }}>
                              No video
                            </span>
                          )}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm #ede8c5'>
                          {String(lesson.completionCount || 0)}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              lesson.isPublished
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {lesson.isPublished ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='flex space-x-2'>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewLessonDetails(lesson);
                              }}
                              className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2'
                              style={{
                                backgroundColor: '#0B222C',
                                border: '1px solid #095D52',
                                color: '#ede8c5',
                                boxShadow:
                                  '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#095D52';
                                e.currentTarget.style.borderColor = '#bbe425';
                                e.currentTarget.style.boxShadow =
                                  '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#0B222C';
                                e.currentTarget.style.borderColor = '#095D52';
                                e.currentTarget.style.boxShadow =
                                  '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                              }}
                              title='View Details'
                            >
                              <svg
                                className='w-4 h-4'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                                />
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                                />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditLesson(lesson);
                              }}
                              className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2'
                              style={{
                                backgroundColor: '#0B222C',
                                border: '1px solid #095D52',
                                color: '#ede8c5',
                                boxShadow:
                                  '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#095D52';
                                e.currentTarget.style.borderColor = '#bbe425';
                                e.currentTarget.style.boxShadow =
                                  '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#0B222C';
                                e.currentTarget.style.borderColor = '#095D52';
                                e.currentTarget.style.boxShadow =
                                  '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                              }}
                              title='Edit Lesson'
                            >
                              <svg
                                className='w-4 h-4'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                                />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteLesson(lesson);
                              }}
                              className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2'
                              style={{
                                backgroundColor: '#0B222C',
                                border: '1px solid #095D52',
                                color: '#ede8c5',
                                boxShadow:
                                  '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#EF4444';
                                e.currentTarget.style.borderColor = '#DC2626';
                                e.currentTarget.style.boxShadow =
                                  '0 4px 15px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(220, 38, 38, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#0B222C';
                                e.currentTarget.style.borderColor = '#095D52';
                                e.currentTarget.style.boxShadow =
                                  '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                              }}
                              title='Delete Lesson'
                            >
                              <svg
                                className='w-4 h-4'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Lessons Pagination */}
          {lessonsTotalPages > 1 && (
            <div className='mt-6' style={{ borderTop: '1px solid #465705' }}>
              <div className='flex justify-between items-center pt-4'>
                <div className='text-sm' style={{ color: '#ede8c5' }}>
                  Page {lessonsCurrentPage} of {lessonsTotalPages} • Showing{' '}
                  {(lessonsCurrentPage - 1) * lessonsPerPage + 1} to{' '}
                  {Math.min(lessonsCurrentPage * lessonsPerPage, lessons.length)} of{' '}
                  {lessons.length} lessons
                </div>

                <div className='flex space-x-2'>
                  <button
                    onClick={() => handleLessonsPageChange(lessonsCurrentPage - 1)}
                    disabled={lessonsCurrentPage === 1}
                    className='flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    style={{
                      backgroundColor: lessonsCurrentPage === 1 ? '#095D52' : '#0B222C',
                      border: '1px solid #bbe425',
                      color: '#ede8c5',
                    }}
                    onMouseEnter={(e) => {
                      if (lessonsCurrentPage !== 1) {
                        e.currentTarget.style.backgroundColor = '#bbe425';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (lessonsCurrentPage !== 1) {
                        e.currentTarget.style.backgroundColor = '#0c222d';
                      }
                    }}
                  >
                    <svg
                      className='w-4 h-4 mr-2'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 19l-7-7 7-7'
                      />
                    </svg>
                    Previous
                  </button>

                  <button
                    onClick={() => handleLessonsPageChange(lessonsCurrentPage + 1)}
                    disabled={lessonsCurrentPage === lessonsTotalPages}
                    className='flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    style={{
                      backgroundColor:
                        lessonsCurrentPage === lessonsTotalPages ? '#095D52' : '#bbe425',
                      border: '1px solid #bbe425',
                      color: '#ede8c5',
                    }}
                    onMouseEnter={(e) => {
                      if (lessonsCurrentPage !== lessonsTotalPages) {
                        e.currentTarget.style.backgroundColor = '#ede8c5';
                        e.currentTarget.style.color = '#0c222d';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (lessonsCurrentPage !== lessonsTotalPages) {
                        e.currentTarget.style.backgroundColor = '#bbe425';
                        e.currentTarget.style.color = '#ede8c5';
                      }
                    }}
                  >
                    Next
                    <svg
                      className='w-4 h-4 ml-2'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M9 5l7 7-7 7'
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Course Modal */}
      {showCreateCourseForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-2xl w-full mx-4'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex justify-between items-center mb-6'>
                <h2
                  className='text-2xl font-bold font-dm-serif'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                    letterSpacing: '0.8px',
                  }}
                >
                  Create New Course
                </h2>
                <button
                  onClick={closeCreateCourseForm}
                  className='text-2xl transition-all duration-300 hover:scale-110'
                  style={{ color: '#ede8c5' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#bbe425')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#ede8c5')}
                >
                  ×
                </button>
              </div>

              <form onSubmit={onSubmitCourse} className='space-y-6'>
                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Title
                  </label>
                  <Input
                    type='text'
                    value={courseTitle}
                    onChange={(e) => setCourseTitle(e.target.value)}
                    className='w-full'
                    placeholder='Enter course title'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Description
                  </label>
                  <textarea
                    value={courseDescription}
                    onChange={(e) => setCourseDescription(e.target.value)}
                    className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                    }}
                    rows={4}
                    placeholder='Enter course description'
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div>
                    <label
                      className='block text-sm font-medium mb-2 font-work-sans'
                      style={{ color: '#ede8c5' }}
                    >
                      Category
                    </label>
                    <select
                      value={courseCategory}
                      onChange={(e) => setCourseCategory(e.target.value)}
                      className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                      style={{
                        backgroundColor: '#0B222C',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow:
                          '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      <option value=''>Select a category</option>
                      <option value='Real Estate'>Real Estate</option>
                      <option value='Physical Assets'>Physical Assets</option>
                      <option value='security'>Security</option>
                      <option value='NFT and Digital'>NFT and Digital</option>
                      <option value='Advanced'>Advanced</option>
                      <option value='Other'>Other</option>
                    </select>
                    {courseCategory === 'Other' && (
                      <div className='mt-2'>
                        <Input
                          type='text'
                          value={courseCustomCategory}
                          onChange={(e) => setCourseCustomCategory(e.target.value)}
                          className='w-full'
                          placeholder='Enter custom category'
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      className='block text-sm font-medium mb-2 font-work-sans'
                      style={{ color: '#ede8c5' }}
                    >
                      Difficulty
                    </label>
                    <select
                      value={courseDifficulty}
                      onChange={(e) => setCourseDifficulty(e.target.value)}
                      className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                      style={{
                        backgroundColor: '#0B222C',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow:
                          '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      <option value='Beginner'>Beginner</option>
                      <option value='Intermediate'>Intermediate</option>
                      <option value='Advanced'>Advanced</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Duration (minutes)
                  </label>
                  <Input
                    type='number'
                    value={courseDuration}
                    onChange={(e) => setCourseDuration(e.target.value)}
                    className='w-full max-w-xs'
                    placeholder='Enter duration in minutes'
                  />
                </div>

                {courseError && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      color: '#DC2626',
                    }}
                  >
                    {courseError}
                  </div>
                )}

                {courseSuccess && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #BBF7D0',
                      color: '#16A34A',
                    }}
                  >
                    {courseSuccess}
                  </div>
                )}

                <div className='flex space-x-3 pt-4'>
                  <Button
                    type='button'
                    onClick={closeCreateCourseForm}
                    className='flex-1'
                    style={{
                      backgroundColor: '#095D52',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                      backgroundImage:
                        'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    className='flex-1'
                    style={{
                      backgroundColor: '#095D52',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                      backgroundImage:
                        'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Create Course
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Lesson Modal */}
      {showCreateLessonForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex justify-between items-center mb-6'>
                <h2
                  className='text-2xl font-bold font-dm-serif'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                    letterSpacing: '0.8px',
                  }}
                >
                  Create New Lesson
                </h2>
                <button
                  onClick={closeCreateLessonForm}
                  className='text-2xl transition-all duration-300 hover:scale-110'
                  style={{ color: '#ede8c5' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#bbe425')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#ede8c5')}
                >
                  ×
                </button>
              </div>

              <form onSubmit={onSubmitLesson} className='space-y-4'>
                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Title
                  </label>
                  <Input
                    type='text'
                    value={lessonTitle}
                    onChange={(e) => setLessonTitle(e.target.value)}
                    className='w-full'
                    placeholder='Enter lesson title'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Description
                  </label>
                  <textarea
                    value={lessonDescription}
                    onChange={(e) => setLessonDescription(e.target.value)}
                    className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                    }}
                    rows={3}
                    placeholder='Enter lesson description'
                  />
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label
                      className='block text-sm font-medium mb-2 font-work-sans'
                      style={{ color: '#ede8c5' }}
                    >
                      Lesson Number
                    </label>
                    <Input
                      type='number'
                      value={lessonNumber}
                      onChange={(e) => setLessonNumber(e.target.value)}
                      className='w-full'
                      placeholder='Enter lesson number'
                    />
                  </div>

                  <div>
                    <label
                      className='block text-sm font-medium mb-2 font-work-sans'
                      style={{ color: '#ede8c5' }}
                    >
                      Duration (minutes)
                    </label>
                    <Input
                      type='number'
                      value={lessonDuration}
                      onChange={(e) => setLessonDuration(e.target.value)}
                      className='w-full'
                      placeholder='Enter duration'
                    />
                  </div>
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Content
                  </label>
                  <textarea
                    value={lessonContent}
                    onChange={(e) => setLessonContent(e.target.value)}
                    className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                    }}
                    rows={4}
                    placeholder='Enter lesson content'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Video URL
                  </label>
                  <Input
                    type='url'
                    value={lessonVideoUrl}
                    onChange={(e) => setLessonVideoUrl(e.target.value)}
                    className='w-full'
                    placeholder='Enter video URL (optional)'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Key Takeaways (one per line)
                  </label>
                  <textarea
                    value={lessonKeyTakeaways}
                    onChange={(e) => setLessonKeyTakeaways(e.target.value)}
                    className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                    }}
                    rows={3}
                    placeholder='Enter key takeaways, one per line'
                  />
                </div>

                {lessonError && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      color: '#DC2626',
                    }}
                  >
                    {lessonError}
                  </div>
                )}

                {lessonSuccess && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #BBF7D0',
                      color: '#16A34A',
                    }}
                  >
                    {lessonSuccess}
                  </div>
                )}

                <div className='flex space-x-3 pt-4'>
                  <Button
                    type='button'
                    onClick={closeCreateLessonForm}
                    className='flex-1'
                    style={{
                      backgroundColor: '#095D52',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                      backgroundImage:
                        'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    className='flex-1'
                    style={{
                      backgroundColor: '#095D52',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                      backgroundImage:
                        'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Create Lesson
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Course Modal */}
      {showEditCourseForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-2xl w-full mx-4'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex justify-between items-center mb-6'>
                <h2
                  className='text-2xl font-bold font-dm-serif'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                    letterSpacing: '0.8px',
                  }}
                >
                  Edit Course
                </h2>
                <button
                  onClick={closeEditCourseForm}
                  className='text-2xl transition-all duration-300 hover:scale-110'
                  style={{ color: '#ede8c5' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#bbe425')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#ede8c5')}
                >
                  ×
                </button>
              </div>

              <form onSubmit={onSubmitEditCourse} className='space-y-6'>
                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Title
                  </label>
                  <Input
                    type='text'
                    value={editCourseTitle}
                    onChange={(e) => setEditCourseTitle(e.target.value)}
                    className='w-full'
                    placeholder='Enter course title'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Description
                  </label>
                  <textarea
                    value={editCourseDescription}
                    onChange={(e) => setEditCourseDescription(e.target.value)}
                    className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                    }}
                    rows={4}
                    placeholder='Enter course description'
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div>
                    <label
                      className='block text-sm font-medium mb-2 font-work-sans'
                      style={{ color: '#ede8c5' }}
                    >
                      Category
                    </label>
                    <select
                      value={editCourseCategory}
                      onChange={(e) => setEditCourseCategory(e.target.value)}
                      className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                      style={{
                        backgroundColor: '#0B222C',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow:
                          '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      <option value=''>Select a category</option>
                      <option value='Real Estate'>Real Estate</option>
                      <option value='Physical Assets'>Physical Assets</option>
                      <option value='security'>Security</option>
                      <option value='NFT and Digital'>NFT and Digital</option>
                      <option value='Advanced'>Advanced</option>
                      <option value='Other'>Other</option>
                    </select>
                    {editCourseCategory === 'Other' && (
                      <div className='mt-2'>
                        <Input
                          type='text'
                          value={editCourseCustomCategory}
                          onChange={(e) => setEditCourseCustomCategory(e.target.value)}
                          className='w-full'
                          placeholder='Enter custom category'
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      className='block text-sm font-medium mb-2 font-work-sans'
                      style={{ color: '#ede8c5' }}
                    >
                      Difficulty
                    </label>
                    <select
                      value={editCourseDifficulty}
                      onChange={(e) => setEditCourseDifficulty(e.target.value)}
                      className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                      style={{
                        backgroundColor: '#0B222C',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow:
                          '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      <option value='Beginner'>Beginner</option>
                      <option value='Intermediate'>Intermediate</option>
                      <option value='Advanced'>Advanced</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Duration (minutes)
                  </label>
                  <Input
                    type='number'
                    value={editCourseDuration}
                    onChange={(e) => setEditCourseDuration(e.target.value)}
                    className='w-full max-w-xs'
                    placeholder='Enter duration in minutes'
                  />
                </div>

                {editCourseError && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      color: '#DC2626',
                    }}
                  >
                    {editCourseError}
                  </div>
                )}

                {editCourseSuccess && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #BBF7D0',
                      color: '#16A34A',
                    }}
                  >
                    {editCourseSuccess}
                  </div>
                )}

                <div className='flex space-x-3 pt-4'>
                  <Button
                    type='button'
                    onClick={closeEditCourseForm}
                    className='flex-1'
                    style={{
                      backgroundColor: '#095D52',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                      backgroundImage:
                        'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    className='flex-1'
                    style={{
                      backgroundColor: '#095D52',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                      backgroundImage:
                        'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Update Course
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Lesson Modal */}
      {showEditLessonForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex justify-between items-center mb-6'>
                <h2
                  className='text-2xl font-bold font-dm-serif'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                    letterSpacing: '0.8px',
                  }}
                >
                  Edit Lesson
                </h2>
                <button
                  onClick={closeEditLessonForm}
                  className='text-2xl transition-all duration-300 hover:scale-110'
                  style={{ color: '#ede8c5' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#bbe425')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#ede8c5')}
                >
                  ×
                </button>
              </div>

              <form onSubmit={onSubmitEditLesson} className='space-y-4'>
                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Title
                  </label>
                  <Input
                    type='text'
                    value={editLessonTitle}
                    onChange={(e) => setEditLessonTitle(e.target.value)}
                    className='w-full'
                    placeholder='Enter lesson title'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Description
                  </label>
                  <textarea
                    value={editLessonDescription}
                    onChange={(e) => setEditLessonDescription(e.target.value)}
                    className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                    }}
                    rows={3}
                    placeholder='Enter lesson description'
                  />
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label
                      className='block text-sm font-medium mb-2 font-work-sans'
                      style={{ color: '#ede8c5' }}
                    >
                      Lesson Number
                    </label>
                    <Input
                      type='number'
                      value={editLessonNumber}
                      onChange={(e) => setEditLessonNumber(e.target.value)}
                      className='w-full'
                      placeholder='Enter lesson number'
                    />
                  </div>

                  <div>
                    <label
                      className='block text-sm font-medium mb-2 font-work-sans'
                      style={{ color: '#ede8c5' }}
                    >
                      Duration (minutes)
                    </label>
                    <Input
                      type='number'
                      value={editLessonDuration}
                      onChange={(e) => setEditLessonDuration(e.target.value)}
                      className='w-full'
                      placeholder='Enter duration'
                    />
                  </div>
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Content
                  </label>
                  <textarea
                    value={editLessonContent}
                    onChange={(e) => setEditLessonContent(e.target.value)}
                    className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                    }}
                    rows={4}
                    placeholder='Enter lesson content'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Video URL
                  </label>
                  <Input
                    type='url'
                    value={editLessonVideoUrl}
                    onChange={(e) => setEditLessonVideoUrl(e.target.value)}
                    className='w-full'
                    placeholder='Enter video URL (optional)'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium mb-2 font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Key Takeaways (one per line)
                  </label>
                  <textarea
                    value={editLessonKeyTakeaways}
                    onChange={(e) => setEditLessonKeyTakeaways(e.target.value)}
                    className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                    }}
                    rows={3}
                    placeholder='Enter key takeaways, one per line'
                  />
                </div>

                {editLessonError && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      color: '#DC2626',
                    }}
                  >
                    {editLessonError}
                  </div>
                )}

                {editLessonSuccess && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #BBF7D0',
                      color: '#16A34A',
                    }}
                  >
                    {editLessonSuccess}
                  </div>
                )}

                <div className='flex space-x-3 pt-4'>
                  <Button
                    type='button'
                    onClick={closeEditLessonForm}
                    className='flex-1'
                    style={{
                      backgroundColor: '#095D52',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                      backgroundImage:
                        'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    className='flex-1'
                    style={{
                      backgroundColor: '#095D52',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                      backgroundImage:
                        'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Update Lesson
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Course Confirmation Modal */}
      {showDeleteConfirm && courseToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-md w-full mx-4'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex items-center mb-4'>
                <div className='flex-shrink-0'>
                  <svg
                    className='w-8 h-8'
                    style={{ color: '#ede8c5' }}
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <h3
                    className='text-lg font-medium font-dm-serif'
                    style={{
                      color: '#ede8c5',
                      textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Delete Course
                  </h3>
                </div>
              </div>

              <div className='mb-6'>
                <p className='text-sm' style={{ color: '#ede8c5' }}>
                  Are you sure you want to delete{' '}
                  <span className='font-semibold' style={{ color: '#ede8c5' }}>
                    "{String(courseToDelete.title || '')}"
                  </span>
                  ?
                </p>
              </div>

              <div className='flex space-x-3'>
                <button
                  onClick={cancelDeleteCourse}
                  className='flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2'
                  style={{
                    color: '#ede8c5',
                    backgroundColor: '#095D52',
                    border: '1px solid #095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#bbe425';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#095D52';
                    e.currentTarget.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteCourse}
                  className='flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2'
                  style={{
                    color: '#ede8c5',
                    backgroundColor: '#095D52',
                    border: '1px solid #095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#bbe425';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#095D52';
                    e.currentTarget.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Delete Course
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Lesson Confirmation Modal */}
      {showDeleteLessonConfirm && lessonToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-md w-full mx-4'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex items-center mb-4'>
                <div className='flex-shrink-0'>
                  <svg
                    className='w-8 h-8'
                    style={{ color: '#ede8c5' }}
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <h3
                    className='text-lg font-medium font-dm-serif'
                    style={{
                      color: '#ede8c5',
                      textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Delete Lesson
                  </h3>
                </div>
              </div>

              <div className='mb-6'>
                <p className='text-sm' style={{ color: '#ede8c5' }}>
                  Are you sure you want to delete{' '}
                  <span className='font-semibold' style={{ color: '#ede8c5' }}>
                    "{String(lessonToDelete.title || '')}"
                  </span>
                  ?
                </p>
              </div>

              <div className='flex space-x-3'>
                <button
                  onClick={cancelDeleteLesson}
                  className='flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2'
                  style={{
                    color: '#ede8c5',
                    backgroundColor: '#095D52',
                    border: '1px solid #095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#bbe425';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#095D52';
                    e.currentTarget.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteLesson}
                  className='flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2'
                  style={{
                    color: '#ede8c5',
                    backgroundColor: '#095D52',
                    border: '1px solid #095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#bbe425';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#095D52';
                    e.currentTarget.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Delete Lesson
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Details Modal */}
      {showLessonDetails && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-4xl w-full max-h-[90vh] flex flex-col'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div
              className='flex justify-between items-center p-6 flex-shrink-0'
              style={{ borderBottom: '1px solid #465705' }}
            >
              <h2
                className='text-2xl font-bold font-dm-serif'
                style={{
                  color: '#ede8c5',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                  letterSpacing: '0.8px',
                }}
              >
                Lesson Details
              </h2>
              <button
                onClick={closeLessonDetails}
                className='text-2xl transition-all duration-300 hover:scale-110'
                style={{ color: '#ede8c5' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#bbe425')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#ede8c5')}
              >
                ×
              </button>
            </div>

            <div className='flex-1 overflow-y-auto p-6'>
              {lessonDetailsLoading ? (
                <div className='flex items-center justify-center py-12'>
                  <div className='text-center'>
                    <div
                      className='animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4'
                      style={{ borderColor: '#465705' }}
                    ></div>
                    <p className='text-lg' style={{ color: '#ede8c5' }}>
                      Loading lesson details...
                    </p>
                  </div>
                </div>
              ) : lessonDetails ? (
                <div className='space-y-6'>
                  {/* Basic Information */}
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    <div className='space-y-4'>
                      <div>
                        <label className='text-sm font-medium text-gray-400 uppercase tracking-wider font-work-sans'>
                          Lesson Number
                        </label>
                        <p
                          className='text-lg font-semibold mt-1 font-dm-serif'
                          style={{ color: '#ede8c5' }}
                        >
                          {String(lessonDetails.lessonNumber || 0)}
                        </p>
                      </div>
                      <div>
                        <label className='text-sm font-medium text-gray-400 uppercase tracking-wider font-work-sans'>
                          Duration
                        </label>
                        <p
                          className='text-lg font-semibold mt-1 font-dm-serif'
                          style={{ color: '#ede8c5' }}
                        >
                          {lessonDetails.duration
                            ? lessonDetails.duration >= 60
                              ? `${Math.floor(lessonDetails.duration / 60)}h ${lessonDetails.duration % 60}m`
                              : `${lessonDetails.duration}m`
                            : '0m'}
                        </p>
                      </div>
                      <div>
                        <label className='text-sm font-medium text-gray-400 uppercase tracking-wider font-work-sans'>
                          Status
                        </label>
                        <div className='mt-1'>
                          <span
                            className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                              lessonDetails.isPublished
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {lessonDetails.isPublished ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className='space-y-4'>
                      <div>
                        <label className='text-sm font-medium text-gray-400 uppercase tracking-wider font-work-sans'>
                          Completion Count
                        </label>
                        <p
                          className='text-lg font-semibold mt-1 font-dm-serif'
                          style={{ color: '#ede8c5' }}
                        >
                          {String(lessonDetails.completionCount || 0)}
                        </p>
                      </div>
                      <div>
                        <label className='text-sm font-medium text-gray-400 uppercase tracking-wider font-work-sans'>
                          Course ID
                        </label>
                        <p className='#ede8c5 text-sm mt-1 font-mono'>
                          {lessonDetails.course
                            ? typeof lessonDetails.course === 'object'
                              ? lessonDetails.course._id || lessonDetails.course.id || 'N/A'
                              : String(lessonDetails.course)
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Title and Description */}
                  <div className='space-y-4'>
                    <div>
                      <label className='text-sm font-medium text-gray-400 uppercase tracking-wider'>
                        Title
                      </label>
                      <p
                        className='text-xl font-bold mt-1 font-dm-serif'
                        style={{ color: '#ede8c5' }}
                      >
                        {String(lessonDetails.title || '')}
                      </p>
                    </div>
                    <div>
                      <label className='text-sm font-medium text-gray-400 uppercase tracking-wider'>
                        Description
                      </label>
                      <p
                        className='mt-1 leading-relaxed font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        {String(lessonDetails.description || '')}
                      </p>
                    </div>
                  </div>

                  {/* Video URL */}
                  {lessonDetails.videoUrl && (
                    <div>
                      <label className='text-sm font-medium text-gray-400 uppercase tracking-wider'>
                        Video URL
                      </label>
                      <div className='mt-1'>
                        <a
                          href={lessonDetails.videoUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors'
                        >
                          <svg className='w-4 h-4 mr-2' fill='currentColor' viewBox='0 0 20 20'>
                            <path d='M10 12a2 2 0 100-4 2 2 0 000 4z' />
                            <path
                              fillRule='evenodd'
                              d='M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z'
                              clipRule='evenodd'
                            />
                          </svg>
                          Watch Video
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div>
                    <label className='text-sm font-medium text-gray-400 uppercase tracking-wider'>
                      Content
                    </label>
                    <div
                      className='mt-1 p-4 rounded-lg'
                      style={{ backgroundColor: '#0B222C', border: '1px solid #095D52' }}
                    >
                      <p
                        className='leading-relaxed whitespace-pre-wrap font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        {String(lessonDetails.content || '')}
                      </p>
                    </div>
                  </div>

                  {/* Key Takeaways */}
                  {lessonDetails.keyTakeaways && lessonDetails.keyTakeaways.length > 0 && (
                    <div>
                      <label className='text-sm font-medium text-gray-400 uppercase tracking-wider'>
                        Key Takeaways
                      </label>
                      <div className='mt-1'>
                        <ul className='space-y-2'>
                          {lessonDetails.keyTakeaways.map((takeaway: string, index: number) => (
                            <li key={index} className='flex items-start'>
                              <span
                                className='flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5'
                                style={{ backgroundColor: '#095D52', color: '#ede8c5' }}
                              >
                                {index + 1}
                              </span>
                              <span className='font-work-sans' style={{ color: '#ede8c5' }}>
                                {takeaway}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className='pt-4' style={{ borderTop: '1px solid #465705' }}>
                    <div
                      className='grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-work-sans'
                      style={{ color: '#ede8c5' }}
                    >
                      <div>
                        <span className='font-medium'>Created:</span>{' '}
                        {lessonDetails.createdAt
                          ? new Date(lessonDetails.createdAt).toLocaleDateString()
                          : 'N/A'}
                      </div>
                      <div>
                        <span className='font-medium'>Updated:</span>{' '}
                        {lessonDetails.updatedAt
                          ? new Date(lessonDetails.updatedAt).toLocaleDateString()
                          : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='text-center py-12'>
                  <p className='text-lg font-work-sans' style={{ color: '#ede8c5' }}>
                    Failed to load lesson details
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursesPage;
